import type { Route, Stop } from '@/@types/gtfs-ressource';
import Common from '@/libs/common';
import { compareRoutes } from '@pysae/transit-core/helpers';
import { isCanonicalStop } from '@/libs/helpers/stop-clusters';

const SEARCH_RESULTS_LIMIT = 20;

export interface SearchIndexEntry<T> {
  item: T;
  /** Normalized (lowercase, unaccented) texts the item can be found by. */
  keys: string[];
}

/**
 * Stops as passengers name them: one per canonical stop, without the platforms
 * merged onto it — the same deduplication as the stops list.
 */
export function buildStopSearchIndex(
  stops: Stop[],
  canonicalOf: { [stopId: string]: string },
): SearchIndexEntry<Stop>[] {
  return stops
    .filter(stop => isCanonicalStop(stop.stop_id, canonicalOf))
    .map(stop => ({ item: stop, keys: [Common.normalize(stop.stop_name)] }))
    .sort((a, b) => a.keys[0].localeCompare(b.keys[0]));
}

export function buildRouteSearchIndex(routes: Route[]): SearchIndexEntry<Route>[] {
  return [...routes].sort(compareRoutes).map(route => ({
    item: route,
    keys: [route.route_short_name ?? '', route.route_long_name ?? ''].map(Common.normalize),
  }));
}

function isWordStart(key: string, index: number): boolean {
  return index === 0 || !/[\p{L}\p{N}]/u.test(key[index - 1]);
}

/** 0 = a key starts with the query, 1 = a word of a key does, 2 = a key contains it. */
function matchRank(keys: string[], query: string): number | undefined {
  let best: number | undefined;
  for (const key of keys) {
    let index = key.indexOf(query);
    while (index >= 0) {
      const rank = index === 0 ? 0 : isWordStart(key, index) ? 1 : 2;
      if (rank === 0) return 0;
      if (best === undefined || rank < best) best = rank;
      index = key.indexOf(query, index + 1);
    }
  }
  return best;
}

/**
 * Items matching the query, best matches first: names starting with the query,
 * then names with a word starting with it, then any other occurrence. Ties keep
 * the index order.
 */
export function searchIndex<T>(
  index: SearchIndexEntry<T>[],
  rawQuery: string,
  limit = SEARCH_RESULTS_LIMIT,
): T[] {
  const query = Common.normalize(rawQuery);
  if (!query) return [];

  const matches: Array<{ rank: number; position: number; item: T }> = [];
  index.forEach((entry, position) => {
    const rank = matchRank(entry.keys, query);
    if (rank !== undefined) matches.push({ rank, position, item: entry.item });
  });

  return matches
    .sort((a, b) => a.rank - b.rank || a.position - b.position)
    .slice(0, limit)
    .map(match => match.item);
}
